import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  Text,
  HStack,
  Icon,
  PopoverTrigger,
  Alert,
  AlertIcon,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { FiInfo } from "react-icons/fi";
import { useState, useEffect, useMemo } from "react";
import { useOrdersStore } from "../store/useOrdersStore";
import services from "../services";
import useNotification from "../../../utils/useNotification";
import GaraazGeneralPopover from "./GaraazGeneralPopover";
import { getCurrentUTCTimestamp } from "../../../utils/dateResolvers";
import { META_INFO_KEYS } from "../../../utils/constants";
import { extractErrorMessage } from "../utils/orderItemsActionUtils";
import { OrderItemActionType } from "../types/orderItemsActionTypes";

type ActionMode = "cancel" | "hold" | "unhold" | "add";
type CancelSource = "active" | "supplierCancelled";

type SupplierCancelledItem = {
  partId?: string;
  _id?: string;
  partNumber?: string;
  orderedQuantity?: number;
  remainingDecisionQuantity?: number;
  cancelledQuantity?: number;
  holdQuantity?: number;
  [key: string]: any;
};

const getSupplierCancelledAvailableQty = (item: SupplierCancelledItem) =>
  Number(item?.remainingDecisionQuantity ?? item?.orderedQuantity ?? 0);
const hasPositiveRemainingDecisionQty = (item: {
  remainingDecisionQuantity?: number;
}) => Number(item?.remainingDecisionQuantity ?? 0) > 0;

// Mode to API action type mapping
const MODE_TO_ACTION_MAP: Record<ActionMode, OrderItemActionType> = {
  cancel: "CANCEL",
  hold: "HOLD",
  unhold: "UNHOLD",
  add: "ADD",
};

type CancelHoldModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isDisabled: boolean;
  orderId: string;
  orderNo: string;
  mode: "cancel" | "hold" | "unhold" | "add" | null;
  supplierCancelledItems?: SupplierCancelledItem[];
  refetchAllOrders?: () => void;
};

const PartActionModal: React.FC<CancelHoldModalProps> = ({
  isOpen,
  onClose,
  isDisabled,
  orderId,
  orderNo,
  mode,
  supplierCancelledItems = [],
  refetchAllOrders,
}) => {
  const toast = useNotification();
  const orderItems = useOrdersStore((state) => state.orderItems[orderId]);

  const [editedItems, setEditedItems] = useState<
    Array<{
      partId: string;
      partNumber: string;
      originalOrderedQuantity: number;
      originalRemainingDecisionQuantity: number;
      originalCancelledQuantity: number;
      originalHoldQuantity: number;
      actionQuantity: number;
    }>
  >([]);
  const [fullOrderItems, setFullOrderItems] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCancelSource, setSelectedCancelSource] = useState<
    CancelSource | ""
  >("");

  const hasActiveCancellable = useMemo(
    () => Array.isArray(orderItems) && orderItems.some(hasPositiveRemainingDecisionQty),
    [orderItems],
  );
  const hasSupplierCancelledCancellable = useMemo(
    () =>
      Array.isArray(supplierCancelledItems) &&
      supplierCancelledItems.some(
        (item: SupplierCancelledItem) =>
          getSupplierCancelledAvailableQty(item) > 0,
      ),
    [supplierCancelledItems],
  );
  const hasActiveHoldable = hasActiveCancellable;
  const hasSupplierCancelledHoldable = hasSupplierCancelledCancellable;
  const hasActiveUnholdable = useMemo(
    () =>
      Array.isArray(orderItems) &&
      orderItems.some((item: any) => Number(item?.holdQuantity || 0) > 0),
    [orderItems],
  );
  const hasSupplierCancelledUnholdable = useMemo(
    () =>
      Array.isArray(supplierCancelledItems) &&
      supplierCancelledItems.some(
        (item: SupplierCancelledItem) => Number(item?.holdQuantity || 0) > 0,
      ),
    [supplierCancelledItems],
  );

  const shouldShowSourceSelector =
    mode === "cancel" || mode === "hold" || mode === "unhold";

  const hasActiveForCurrentMode =
    mode === "cancel"
      ? hasActiveCancellable
      : mode === "hold"
        ? hasActiveHoldable
        : mode === "unhold"
          ? hasActiveUnholdable
          : false;

  const hasSupplierCancelledForCurrentMode =
    mode === "cancel"
      ? hasSupplierCancelledCancellable
      : mode === "hold"
        ? hasSupplierCancelledHoldable
        : mode === "unhold"
          ? hasSupplierCancelledUnholdable
          : false;

  const remove_idFromPartDetails = (partObject: any) => {
    return {
      "Part Name": partObject?.partName,
      "Part Number": partObject.partNumber,
      MRP: partObject.mrp,
      Brand: partObject.brandName || partObject.brandId || "N/A",
      "HSN code": partObject.hsnCode,
      "Root Part Number": partObject.rootPartNumber,
    };
  };

  useEffect(() => {
    if (!isOpen) return;

    if (shouldShowSourceSelector) {
      if (!hasActiveForCurrentMode && hasSupplierCancelledForCurrentMode) {
        setSelectedCancelSource("supplierCancelled");
      } else if (
        hasActiveForCurrentMode &&
        !hasSupplierCancelledForCurrentMode
      ) {
        setSelectedCancelSource("active");
      } else if (
        hasActiveForCurrentMode &&
        hasSupplierCancelledForCurrentMode
      ) {
        setSelectedCancelSource("");
      } else {
        setSelectedCancelSource("");
      }
    } else {
      setSelectedCancelSource("");
    }
  }, [
    isOpen,
    mode,
    shouldShowSourceSelector,
    hasActiveForCurrentMode,
    hasSupplierCancelledForCurrentMode,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (shouldShowSourceSelector && !selectedCancelSource) {
      setFullOrderItems([]);
      setEditedItems([]);
      setValidationErrors({});
      return;
    }

    const isSupplierCancelledSource =
      selectedCancelSource === "supplierCancelled";
    const sourceItems = isSupplierCancelledSource
      ? supplierCancelledItems
      : (orderItems ?? []);

    if (!Array.isArray(sourceItems)) {
      setFullOrderItems([]);
      setEditedItems([]);
      setValidationErrors({});
      return;
    }

    setFullOrderItems(sourceItems);
    const initialItems = sourceItems.map((item: any) => {
      const orderedQuantity = Number(item?.orderedQuantity || 0);
      const cancelledQuantity = Number(item?.cancelledQuantity || 0);
      const holdQuantity = Number(item?.holdQuantity || 0);
      const remainingDecisionQuantity = isSupplierCancelledSource
        ? getSupplierCancelledAvailableQty(item)
        : Number(item?.remainingDecisionQuantity ?? 0);

      return {
        partId: item.partId || item._id,
        partNumber: item.partNumber,
        originalOrderedQuantity: orderedQuantity,
        originalRemainingDecisionQuantity: remainingDecisionQuantity,
        originalCancelledQuantity: cancelledQuantity,
        originalHoldQuantity: holdQuantity,
        actionQuantity: 0,
      };
    });
    setEditedItems(initialItems);
    setValidationErrors({});
  }, [
    isOpen,
    orderItems,
    mode,
    selectedCancelSource,
    supplierCancelledItems,
    shouldShowSourceSelector,
  ]);

  const handleQuantityChange = (partId: string, value: string) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    const updatedItems = editedItems.map((item) => {
      if (item.partId !== partId) return item;

      let finalValue = numValue;

      // Apply max value validation based on current mode
      if (mode === "cancel" || mode === "hold") {
        finalValue = Math.min(numValue, item.originalRemainingDecisionQuantity);
      } else if (mode === "unhold") {
        finalValue = Math.min(numValue, item.originalHoldQuantity);
      }
      // For "add" mode: no max limit, only non-negative validation (already handled above)

      return {
        ...item,
        actionQuantity: finalValue,
      };
    });

    setEditedItems(updatedItems);

    // Validate this item
    const changedItem = updatedItems.find((i) => i.partId === partId);
    if (!changedItem) return;

    const errors = { ...validationErrors };

    if (mode === "cancel" || mode === "hold") {
      if (
        changedItem.actionQuantity >
        changedItem.originalRemainingDecisionQuantity
      ) {
        errors[partId] =
          `Value cannot exceed remaining decision quantity (${changedItem.originalRemainingDecisionQuantity})`;
      } else {
        delete errors[partId];
      }
    } else if (mode === "unhold") {
      if (changedItem.actionQuantity > changedItem.originalHoldQuantity) {
        errors[partId] =
          `Value cannot exceed hold quantity (${changedItem.originalHoldQuantity})`;
      } else {
        delete errors[partId];
      }
    } else if (mode === "add") {
      // For add mode, only non-negative validation (already enforced by Math.max(0, ...))
      delete errors[partId];
    }

    setValidationErrors(errors);
  };

  const fullOrderItemsByPartId = useMemo(() => {
    const map = new Map<string, any>();
    fullOrderItems.forEach((fi: any) => {
      const key = fi?.partId || fi?._id;
      if (key) {
        map.set(String(key), fi);
      }
    });
    return map;
  }, [fullOrderItems]);

  const validateAll = (): boolean => {
    const errors: Record<string, string> = {};

    editedItems.forEach((item) => {
      if (mode === "cancel" || mode === "hold") {
        const maxQuantity = item.originalRemainingDecisionQuantity;
        if (item.actionQuantity > maxQuantity) {
          errors[item.partId] =
            `Value cannot exceed remaining decision quantity (${maxQuantity})`;
        }
      } else if (mode === "unhold") {
        if (item.actionQuantity > item.originalHoldQuantity) {
          errors[item.partId] =
            `Value cannot exceed hold quantity (${item.originalHoldQuantity})`;
        }
      }
      // For "add" mode: only non-negative validation (already enforced in handleQuantityChange)
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildActionItems = () => {
    return editedItems
      .filter((item) => item.actionQuantity > 0)
      .map((item) => ({
        partId: item.partId,
        quantity: item.actionQuantity,
      }));
  };

  const handleConfirm = async () => {
    if (!validateAll()) {
      toast({
        description: "Please fix validation errors before confirming",
        status: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const actionItems = buildActionItems();

      if (actionItems.length > 0 && mode) {
        const actionType = MODE_TO_ACTION_MAP[mode];
        const orderItemsActionMode =
          (mode === "cancel" || mode === "hold" || mode === "unhold") &&
          selectedCancelSource === "supplierCancelled"
            ? "reassign"
            : undefined;
        await services.orderItemsAction(
          orderNo,
          actionType,
          actionItems,
          orderItemsActionMode,
        );

        // Special handling for cancel mode: update cancelled timestamp
        if (mode === "cancel") {
          await services.updateOrderMetaInfo(orderId, {
            [META_INFO_KEYS.CANCELLED_AT]: getCurrentUTCTimestamp(),
          });
        }
      }

      // Refetch all orders once at the end to get fully populated data
      if (refetchAllOrders) {
        refetchAllOrders();
      }

      toast({
        description: "Order items updated successfully",
        status: "success",
      });
      onClose();
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast({
        description: errorMessage,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmButtonDisabled = useMemo(() => {
    if (
      isDisabled ||
      isSubmitting ||
      Object.keys(validationErrors).length > 0 ||
      (shouldShowSourceSelector &&
        hasActiveForCurrentMode &&
        hasSupplierCancelledForCurrentMode &&
        !selectedCancelSource)
    ) {
      return true;
    }

    // Check if any item has actionQuantity > 0 for all modes
    const hasAnyNonZeroInput = editedItems.some(
      (item) => item.actionQuantity > 0,
    );
    return !hasAnyNonZeroInput;
  }, [
    isDisabled,
    isSubmitting,
    validationErrors,
    editedItems,
    shouldShowSourceSelector,
    hasActiveForCurrentMode,
    hasSupplierCancelledForCurrentMode,
    selectedCancelSource,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"4xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === "cancel"
            ? "Cancel Items"
            : mode === "hold"
              ? "Hold Items"
              : mode === "unhold"
                ? "Re-Activate Items"
                : "Add Quantity"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box>
            {shouldShowSourceSelector && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {mode === "cancel"
                    ? "Which quantity do you want to cancel?"
                    : mode === "hold"
                      ? "Which quantity do you want to hold?"
                      : "Which quantity do you want to re-activate?"}
                </Text>
                <RadioGroup
                  value={selectedCancelSource}
                  onChange={(value) =>
                    setSelectedCancelSource(value as CancelSource)
                  }
                >
                  <Stack direction="row" spacing={6}>
                    <Radio value="active" isDisabled={!hasActiveForCurrentMode}>
                      {mode === "unhold" ? "Hold Quantity" : "Active Quantity"}
                    </Radio>
                    <Radio
                      value="supplierCancelled"
                      isDisabled={!hasSupplierCancelledForCurrentMode}
                    >
                      {mode === "unhold"
                        ? "Supplier Cancelled Hold Quantity"
                        : "Supplier Cancelled Quantity"}
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>
            )}
            {shouldShowSourceSelector && selectedCancelSource && (
              <HStack spacing={2} mb={3} color="gray.500" alignItems="center">
                <Icon as={FiInfo} boxSize={4} />
                <Text fontSize="sm">
                  {mode === "cancel" &&
                    (selectedCancelSource === "active"
                      ? "Showing parts available to cancel from Active Quantity."
                      : "Showing parts available to cancel from Supplier Cancelled Quantity.")}
                  {mode === "hold" &&
                    (selectedCancelSource === "active"
                      ? "Showing parts available to hold from Active Quantity."
                      : "Showing parts available to hold from Supplier Cancelled Quantity.")}
                  {mode === "unhold" &&
                    (selectedCancelSource === "active"
                      ? "Showing parts available to re-activate from Hold Quantity."
                      : "Showing parts available to re-activate from Supplier Cancelled Hold Quantity.")}
                </Text>
              </HStack>
            )}
            {shouldShowSourceSelector &&
              hasActiveForCurrentMode &&
              hasSupplierCancelledForCurrentMode &&
              !selectedCancelSource && (
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Select a quantity source to load parts.
                </Text>
              )}
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Part Number</Th>
                    {mode === "add" ? (
                      <>
                        <Th isNumeric textAlign="center">
                          <Text textAlign="center">New Ordered Quantity</Text>
                        </Th>
                        <Th isNumeric textAlign="center">
                          <Text textAlign="center">Add Quantity</Text>
                        </Th>
                      </>
                    ) : mode === "unhold" ? (
                      <>
                        <Th isNumeric textAlign="center">
                          <Text textAlign="center">Hold Qty</Text>
                        </Th>
                        <Th isNumeric textAlign="center">
                          <Text textAlign="center">Re-Activate</Text>
                        </Th>
                      </>
                    ) : (
                      <>
                        <Th isNumeric textAlign="center">
                          <Text textAlign="center">Pending Decision</Text>
                        </Th>
                        {mode === "cancel" && (
                          <Th isNumeric>
                            <Text textAlign="center">Cancelled </Text>
                          </Th>
                        )}
                        {mode === "hold" && (
                          <Th isNumeric textAlign="center">
                            <Text textAlign="center">Hold </Text>
                          </Th>
                        )}
                      </>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {editedItems
                    .filter((item) =>
                      mode === "unhold"
                        ? item.originalHoldQuantity > 0
                        : mode === "cancel"
                          ? item.originalRemainingDecisionQuantity > 0
                          : mode === "hold"
                            ? item.originalRemainingDecisionQuantity > 0
                            : true,
                    )
                    .map((item) => {
                      const fullItem = fullOrderItemsByPartId.get(item.partId) || {};
                      const hasError = validationErrors[item.partId];
                      return (
                        <Tr key={item.partId}>
                          <Td>
                            {fullItem.partNumber ? (
                              <GaraazGeneralPopover
                                ArekeysVisible={true}
                                CopierKeys={[]}
                                title="Part Details"
                                value={[remove_idFromPartDetails(fullItem)]}
                              >
                                <PopoverTrigger>
                                  <Text
                                    cursor={"pointer"}
                                    noOfLines={1}
                                    whiteSpace={"normal"}
                                    fontSize={"small"}
                                    color={"#1a202c"}
                                  >
                                    {item.partNumber}
                                  </Text>
                                </PopoverTrigger>
                              </GaraazGeneralPopover>
                            ) : (
                              <Text
                                noOfLines={1}
                                whiteSpace={"normal"}
                                fontSize={"small"}
                                color={"#1a202c"}
                              >
                                {item.partNumber}
                              </Text>
                            )}
                          </Td>
                          {mode === "add" && (
                            <>
                              <Td isNumeric textAlign="center">
                                <Text textAlign="center">
                                  {item.originalOrderedQuantity +
                                    item.actionQuantity}
                                </Text>
                              </Td>
                              <Td isNumeric textAlign="center">
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                >
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    size="sm"
                                    value={Number(item.actionQuantity || "")}
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        item.partId,
                                        e.target.value,
                                      )
                                    }
                                    min={0}
                                    width="100px"
                                    isDisabled={isDisabled || isSubmitting}
                                    isInvalid={!!hasError}
                                    errorBorderColor="red.300"
                                  />
                                </Box>
                              </Td>
                            </>
                          )}
                          {mode !== "unhold" && mode !== "add" && (
                            <Td isNumeric textAlign="center">
                              <Text textAlign="center">
                                {item.originalRemainingDecisionQuantity}
                              </Text>
                            </Td>
                          )}

                          {mode === "cancel" && (
                            <Td isNumeric textAlign="center">
                              <Box display="flex" justifyContent="center">
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  size="sm"
                                  value={Number(item.actionQuantity || "")}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.partId,
                                      e.target.value,
                                    )
                                  }
                                  min={0}
                                  max={item.originalRemainingDecisionQuantity}
                                  width="100px"
                                  isDisabled={isDisabled || isSubmitting}
                                  isInvalid={!!hasError}
                                  errorBorderColor="red.300"
                                />
                              </Box>
                            </Td>
                          )}

                          {mode === "hold" && (
                            <Td isNumeric textAlign="center">
                              <Box display="flex" justifyContent="center">
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  size="sm"
                                  value={Number(item.actionQuantity || "")}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.partId,
                                      e.target.value,
                                    )
                                  }
                                  min={0}
                                  max={item.originalRemainingDecisionQuantity}
                                  width="100px"
                                  isDisabled={isDisabled || isSubmitting}
                                  isInvalid={!!hasError}
                                  errorBorderColor="red.300"
                                />
                              </Box>
                            </Td>
                          )}

                          {mode === "unhold" && (
                            <>
                              <Td isNumeric textAlign="center">
                                <Text textAlign="center">
                                  {item.originalHoldQuantity}
                                </Text>
                              </Td>
                              <Td isNumeric textAlign="center">
                                <Box display="flex" justifyContent="center">
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    size="sm"
                                    value={Number(item.actionQuantity || "")}
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        item.partId,
                                        e.target.value,
                                      )
                                    }
                                    min={0}
                                    max={item.originalHoldQuantity}
                                    width="100px"
                                    isDisabled={isDisabled || isSubmitting}
                                    isInvalid={!!hasError}
                                    errorBorderColor="red.300"
                                  />
                                </Box>
                              </Td>
                            </>
                          )}
                        </Tr>
                      );
                    })}
                </Tbody>
              </Table>
            </TableContainer>
            {Object.keys(validationErrors).length > 0 && (
              <Alert style={{ padding: 1, paddingLeft: 5 }} status="error">
                <AlertIcon />
                <Text fontSize="xs" color="red">
                  {Object.values(validationErrors)[0]}
                </Text>
              </Alert>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleConfirm}
            isDisabled={isConfirmButtonDisabled}
            isLoading={isSubmitting}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PartActionModal;
